import { type RefObject } from "react";
import ApplyContents from "@components/job_post/apply_contents.tsx";
import { JobHeader } from "@components/job_post/application_form_header.tsx";
import EmptyState from "@components/empty_state.tsx";
import { Error } from "@components/error.tsx";
import Fingerprint1 from "@images/fingerprint_1.svg";
import LoadingSpinner from "@sprout/loading_spinner.tsx";
import { IconButton } from "@sprout/icon_button.tsx";
import BackIcon from "@sprout/icons/back.tsx";
import useTranslation from "@hooks/use_translation.ts";
import useDevice from "@hooks/use_device.ts";
import type { JobPostDetailStatus } from "@hooks/use_job_post_detail.ts";
import type { JobPostDetail } from "@/types/job_search/detail";
import type { JobPost } from "@/types/job_search/search";

type Props = {
  selectedPost: JobPost | null;
  detail: JobPostDetail | null;
  status: JobPostDetailStatus;
  formKey: number;
  onSuccess: () => void;
  onBack: () => void;
  dirtyRef: RefObject<() => boolean>;
  scrollRef: RefObject<HTMLDivElement | null>;
  onCreateAlert?: () => void;
};

const PendingHeader = ({ post }: { post: JobPost }) => (
  <JobHeader
    title={post.title}
    companyName={post.companyName}
    logoUrl={post.logoUrl}
    publicUrl={post.publicUrl}
    locations={post.locations}
    workType={post.workType}
    payRange={post.payRanges}
    firstPublished={post.firstPublished}
  />
);

export const JobDetailPanel = ({
  selectedPost,
  detail,
  status,
  formKey,
  onSuccess,
  onBack,
  dirtyRef,
  scrollRef,
  onCreateAlert,
}: Props) => {
  const { t } = useTranslation("jobSearch");
  const device = useDevice();

  return (
    <div
      ref={scrollRef}
      data-provides="job-detail-panel"
      className="flex-grow h-full min-[900px]:overflow-auto bg-white rounded-lg p-6"
    >
      {device.isMobile && (
        <div className="mb-4">
          <IconButton icon={BackIcon} size="md" label={t("detailPanel.back")} onClick={onBack} />
        </div>
      )}
      {status === "idle" && (
        <EmptyState
          svg={Fingerprint1}
          title={t("detailPanel.idleTitle")}
          message={t("detailPanel.idleMessage")}
        />
      )}
      {status === "error" && (
        <div className="flex justify-center w-full">
          <Error header={t("detailPanel.errorHeader")} />
        </div>
      )}
      {status === "loading" && (
        <div className="flex flex-col gap-6">
          {selectedPost && <PendingHeader post={selectedPost} />}
          <div className="flex justify-center py-12">
            <LoadingSpinner color="green" />
          </div>
        </div>
      )}
      {status === "ready" && detail && (
        <ApplyContents
          key={`${detail.jobPost.fingerprint}-${formKey}`}
          detail={detail}
          submitted={false}
          onSuccess={onSuccess}
          onViewJobs={() => {}}
          dirtyRef={dirtyRef}
          summary={selectedPost?.summary ?? detail.jobPost.summary}
          onCreateAlert={onCreateAlert}
        />
      )}
    </div>
  );
};

export default JobDetailPanel;
